import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  UserCircle2,
  Calendar,
  Clock,
  Briefcase,
  KeyRound,
  Camera,
  UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PlayerService } from "@/lib/services/player";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useListRolesQuery, RoleDto } from "@/store/apiSlice";
import { TeamBadge } from "@/components/ui/TeamBadge";

interface PlayerProfile {
  id: number;
  username: string;
  email: string;
  full_name: string;
  team?: string | null;
  role?: string | null;
  avatar?: {
    image_url?: string;
  } | null;
  bio?: string | null;
  is_verified: boolean;
  is_active: boolean;
  date_joined: string;
  last_login?: string | null;
  updated_at: string;
  profile?: {
    date_of_birth?: string | null;
    gender?: string | null;
    job_title?: string | null;
    company?: string | null;
    department?: string | null;
  } | null;
}

export default function PlayerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isTogglingVerified, setIsTogglingVerified] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("" as number | "");
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const { data: rolesData = [] } = useListRolesQuery();
  const roles = rolesData.map((r) => ({
    id: r.id,
    name: r.name ?? "",
    code: r.code ?? "",
    color_code: r.color_code,
  }));

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    setIsLoading(true);
    setError(null);

    PlayerService.getPlayerById(id)
      .then((data) => {
        if (!mounted) return;
        setPlayer(data as PlayerProfile);
      })
      .catch((e: unknown) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load player profile");
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  if (!id) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Invalid player.</p>
        <Button asChild className="mt-4" variant="ghost">
          <Link to="/players">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Players
          </Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto px-4 py-8 space-y-4">
        <div>
          <Button variant="ghost" className="mb-4" disabled>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Players
          </Button>
        </div>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="mx-auto px-4 py-8 space-y-4">
        <Link to="/players">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Players
          </Button>
        </Link>
        <Card className="border-destructive/40">
          <CardContent className="p-6">
            <p className="text-sm text-destructive">
              {error ?? "Player not found or failed to load profile."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const avatarUrl = player.avatar?.image_url;
  const findTeamObj = () => {
    if (!player) return null;
    const t = player.team ?? player.role;
    if (t === null || t === undefined) return null;

    if (typeof t === "number") {
      return roles.find((r) => r.id === t) || null;
    }

    const parsedId = Number(t);
    if (!Number.isNaN(parsedId) && roles.some((r) => r.id === parsedId)) {
      return roles.find((r) => r.id === parsedId) || null;
    }

    const label = String(t).trim().toLowerCase();
    if (!label) return null;
    return roles.find((r) => (r.name ?? "").toLowerCase() === label || (r.code ?? "").toLowerCase() === label) || null;
  };

  const teamObj = findTeamObj();
  const teamLabel = teamObj ? (teamObj.name || teamObj.code) : "Unassigned";

  const syncTeamSelectToPlayer = () => {
    const teamObj = findTeamObj();
    setSelectedRoleId(teamObj ? teamObj.id : ("" as number | ""));
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  const handleSubmitPasswordChange = async () => {
    if (!id) return;
    if (!newPassword || !confirmPassword) {
      return;
    }
    if (newPassword !== confirmPassword) {
      // In a real app, you might show inline validation; keeping it simple here.
      return;
    }

    try {
      setIsChangingPassword(true);
      await PlayerService.changePassword(id, {
        password: newPassword,
        confirm_password: confirmPassword,
      });
      setIsPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      console.error("Failed to change password", e);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="mx-auto px-4 py-8 space-y-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link to="/players">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          {/* <Link to={`/players/${id}/report`}>
            <Button variant="outline" size="sm">View Reports</Button>
          </Link> */}
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setIsPasswordDialogOpen(true)}
              disabled={isChangingPassword}
            >
              <KeyRound className="h-3.5 w-3.5 mr-1" />
              Change Password
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Password</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    New password
                  </label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Confirm password
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPasswordDialogOpen(false)}
                  disabled={isChangingPassword}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSubmitPasswordChange}
                  disabled={isChangingPassword || !newPassword || !confirmPassword}
                >
                  {isChangingPassword ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button asChild size="sm" variant="outline">
            <Link to={`/players/${id}/edit`}>Edit Profile</Link>
          </Button>
        </div>
      </div>

      {/* Header card */}
      <Card className="border-border shadow-sm bg-card">
        <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="relative">
              <div className="h-20 w-20 rounded-full border border-border overflow-hidden bg-muted flex items-center justify-center text-primary">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={player.full_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <UserCircle2 className="h-10 w-10" />
                )}
              </div>
              <Button
                asChild
                type="button"
                size="icon"
                variant="secondary"
                className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full shadow-md"
              >
                <Link to={`/players/${id}/edit`}>
                  <Camera className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-start gap-2">
                <h1 className="text-3xl font-bold text-foreground">{player.full_name}</h1>
                <Badge
                  variant={player.is_active ? "secondary" : "outline"}
                  className="text-xs font-medium"
                >
                  {player.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{player.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {player.username && (
                  <Badge variant="outline" className="font-mono">
                    @{player.username}
                  </Badge>
                )}
                <Badge variant="outline">
                  Joined: {formatDate(player.date_joined).split(",")[0]}
                </Badge>
                <Badge
                  variant={player.is_verified ? "secondary" : "outline"}
                >
                  {player.is_verified ? "Verified" : "Unverified"}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {/* <Button asChild size="sm" variant="outline">
              <Link to={`/players/${id}/report`}>Open Report</Link>
            </Button> */}
          </div>
        </CardContent>
      </Card>

      {/* Account Status */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">Account Status</h2>
            <div className="flex items-center gap-2">
              <Badge variant={player.is_active ? "secondary" : "outline"} className="text-xs">
                {player.is_active ? "Active" : "Inactive"}
              </Badge>
              <Badge variant={player.is_verified ? "secondary" : "outline"} className="text-xs">
                {player.is_verified ? "Verified" : "Unverified"}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border/60 p-3 bg-muted">
              <p className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Active</p>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-sm font-medium">{player.is_active ? "Enabled" : "Disabled"}</span>
                <Switch checked={player.is_active} disabled={isTogglingStatus} onCheckedChange={async (checked: boolean) => {
                  if (!id) return;
                  try {
                    setIsTogglingStatus(true);
                    await PlayerService.adminQuickUpdate(id, { is_active: checked });
                    setPlayer({ ...player, is_active: checked });
                    toast.success(
                      checked
                        ? "User account activated."
                        : "User account deactivated.",
                    );
                  } catch (e) {
                    console.error(e);
                    toast.error("Failed to update active status.");
                  } finally {
                    setIsTogglingStatus(false);
                  }
                }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Controls whether player can log in.</p>
            </div>
            <div className="rounded-xl border border-border/60 p-3 bg-muted">
              <p className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Verified</p>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-sm font-medium">{player.is_verified ? "Verified" : "Unverified"}</span>
                <Switch checked={player.is_verified} disabled={isTogglingVerified} onCheckedChange={async (checked: boolean) => {
                  if (!id) return;
                  try {
                    setIsTogglingVerified(true);
                    await PlayerService.adminQuickUpdate(id, { is_verified: checked });
                    setPlayer({ ...player, is_verified: checked });
                    toast.success(
                      checked
                        ? "User marked as verified."
                        : "User marked as unverified.",
                    );
                  } catch (e) {
                    console.error(e);
                    toast.error("Failed to update verification status.");
                  } finally {
                    setIsTogglingVerified(false);
                  }
                }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Set verification status for trust and approvals.</p>
            </div>
            <div className="rounded-xl border border-border/60 p-3 bg-muted">
              <p className="text-xs text-muted-foreground uppercase tracking-[0.2em]">Team</p>
              <div className="mt-1">
                <TeamBadge name={teamLabel} colorCode={teamObj?.color_code} />
              </div>
              <Dialog
                open={teamDialogOpen}
                onOpenChange={(open) => {
                  setTeamDialogOpen(open);
                  if (open) syncTeamSelectToPlayer();
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="mt-2 h-8 gap-1.5 px-2.5 text-xs shadow-sm w-fit"
                  >
                    <UsersRound className="h-3 w-3 shrink-0" />
                    Change Team
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Change user team</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Select team</p>
                      <select
                        className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
                        value={selectedRoleId === "" ? "" : String(selectedRoleId)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedRoleId(v ? Number(v) : ("" as number | ""));
                        }}
                      >
                        <option value="">Select team</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name || r.code}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTeamDialogOpen(false);
                        syncTeamSelectToPlayer();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!selectedRoleId}
                      onClick={async () => {
                        if (!id || !selectedRoleId) return;
                        try {
                          await PlayerService.adminQuickUpdate(id, {
                            team_id: Number(selectedRoleId),
                          });
                          const picked = roles.find((r) => r.id === Number(selectedRoleId));
                          const label = picked?.name || picked?.code || teamLabel;
                          setPlayer({
                            ...player,
                            team: label,
                            role: label,
                          });
                          toast.success("Team updated.");
                          setTeamDialogOpen(false);
                        } catch (e) {
                          console.error(e);
                          toast.error("Failed to update team.");
                        }
                      }}
                    >
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meta stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Joined</p>
              <p className="text-sm font-semibold text-foreground">
                {formatDate(player.date_joined)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Last Login</p>
              <p className="text-sm font-semibold text-foreground">
                {formatDate(player.last_login)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Last Updated</p>
              <p className="text-sm font-semibold text-foreground">
                {formatDate(player.updated_at)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profile details */}
      {/* <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            Profile Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Full name</p>
              <p className="font-medium text-foreground">{player.full_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Team</p>
              <p className="font-medium text-foreground">{teamLabel === "Unassigned" ? "—" : teamLabel}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Company</p>
              <p className="font-medium text-foreground">
                {player.profile?.company ?? "—"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Department</p>
              <p className="font-medium text-foreground">
                {player.profile?.department ?? "—"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Job title</p>
              <p className="font-medium text-foreground">
                {player.profile?.job_title ?? "—"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Date of birth</p>
              <p className="font-medium text-foreground">
                {player.profile?.date_of_birth
                  ? formatDate(player.profile.date_of_birth)
                  : "—"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Gender</p>
              <p className="font-medium text-foreground">
                {player.profile?.gender ?? "—"}
              </p>
            </div>
            <div className="space-y-1 md:col-span-2">
              <p className="text-xs text-muted-foreground">Bio</p>
              <p className="font-medium text-foreground">
                {player.bio || "No bio added yet."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
}
